<template>
  <div class="relative w-full">
    <!-- Top Header with Logo -->
    <header ref="headerElement" class="relative bg-white shadow-sm py-8 overflow-hidden w-full">
      <!-- Solar System Pattern Background -->
      <div class="absolute inset-0 opacity-2">
        <svg class="w-full h-full" preserveAspectRatio="none" viewBox="0 0 1200 200" xmlns="http://www.w3.org/2000/svg">
          <!-- Background Grid Pattern -->
          <defs>
            <pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse">
              <path d="M 20 0 L 0 0 0 20" fill="none" stroke="#C8E6C9" stroke-width="0.5" opacity="0.8"/>
            </pattern>
            <pattern id="fineGrid" width="5" height="5" patternUnits="userSpaceOnUse">
              <path d="M 5 0 L 0 0 0 5" fill="none" stroke="#E8F5E8" stroke-width="0.3" opacity="0.6"/>
            </pattern>
          </defs>
          
          <!-- Grid Layers -->
          <rect width="100%" height="100%" fill="url(#fineGrid)" opacity="0.7"/>
          <rect width="100%" height="100%" fill="url(#grid)" opacity="0.8"/>
          
          <!-- Coordinate Lines (Major) -->
          <line x1="600" y1="0" x2="600" y2="200" stroke="#81C784" stroke-width="1" opacity="0.6" stroke-dasharray="3,6"/>
          <line x1="0" y1="100" x2="1200" y2="100" stroke="#81C784" stroke-width="1" opacity="0.6" stroke-dasharray="3,6"/>
          
          <!-- Coordinate Lines (Minor) -->
          <line x1="300" y1="0" x2="300" y2="200" stroke="#A5D6A7" stroke-width="0.6" opacity="0.5" stroke-dasharray="2,4"/>
          <line x1="900" y1="0" x2="900" y2="200" stroke="#A5D6A7" stroke-width="0.6" opacity="0.5" stroke-dasharray="2,4"/>
          <line x1="0" y1="50" x2="1200" y2="50" stroke="#A5D6A7" stroke-width="0.6" opacity="0.5" stroke-dasharray="2,4"/>
          <line x1="0" y1="150" x2="1200" y2="150" stroke="#A5D6A7" stroke-width="0.6" opacity="0.5" stroke-dasharray="2,4"/>
          
          <!-- Grid Intersection Points -->
          <circle cx="300" cy="50" r="1.2" fill="#A5D6A7" opacity="0.4"/>
          <circle cx="600" cy="50" r="1.2" fill="#A5D6A7" opacity="0.4"/>
          <circle cx="900" cy="50" r="1.2" fill="#A5D6A7" opacity="0.4"/>
          <circle cx="300" cy="100" r="1.5" fill="#81C784" opacity="0.5"/>
          <circle cx="600" cy="100" r="1.5" fill="#81C784" opacity="0.5"/>
          <circle cx="900" cy="100" r="1.5" fill="#81C784" opacity="0.5"/>
          <circle cx="300" cy="150" r="1.2" fill="#A5D6A7" opacity="0.4"/>
          <circle cx="600" cy="150" r="1.2" fill="#A5D6A7" opacity="0.4"/>
          <circle cx="900" cy="150" r="1.2" fill="#A5D6A7" opacity="0.4"/>
          
          <!-- Orbital Paths -->
          <ellipse cx="600" cy="100" rx="150" ry="60" fill="none" stroke="#E8F5E8" stroke-width="1" opacity="0.3" stroke-dasharray="5,5"/>
          <ellipse cx="600" cy="100" rx="250" ry="90" fill="none" stroke="#E3F2FD" stroke-width="1" opacity="0.25" stroke-dasharray="3,7"/>
          <ellipse cx="600" cy="100" rx="350" ry="120" fill="none" stroke="#FFF3E0" stroke-width="1" opacity="0.2" stroke-dasharray="7,3"/>
          

          <!-- Central Sun with Core Network -->
          <g>
            <circle cx="600" cy="100" r="12" fill="#FFD54F" class="solar-sun" opacity="0.6">
              <animate attributeName="r" values="12;15;12" dur="8s" repeatCount="indefinite"/>
              <animate attributeName="fill" values="#FFD54F;#FFEB3B;#FFD54F" dur="10s" repeatCount="indefinite"/>
            </circle>
            
            <!-- Core micro-nodes around Central Sun -->
            <g>
              <animateTransform attributeName="transform" type="rotate" values="0 600 100;360 600 100" dur="3s" repeatCount="indefinite"/>
              <circle cx="618" cy="100" r="2.5" fill="#FFF176" opacity="0.8">
                <animate attributeName="r" values="2.5;3.5;2.5" dur="2s" repeatCount="indefinite"/>
              </circle>
              <circle cx="582" cy="100" r="2.2" fill="#FFEE58" opacity="0.7">
                <animate attributeName="r" values="2.2;3.2;2.2" dur="2.2s" repeatCount="indefinite"/>
              </circle>
              <circle cx="600" cy="82" r="2" fill="#FFEB3B" opacity="0.7">
                <animate attributeName="r" values="2;3;2" dur="1.8s" repeatCount="indefinite"/>
              </circle>
              <circle cx="600" cy="118" r="2.3" fill="#FDD835" opacity="0.6">
                <animate attributeName="r" values="2.3;3.3;2.3" dur="2.1s" repeatCount="indefinite"/>
              </circle>
              
              <!-- Core connections -->
              <line x1="600" y1="100" x2="618" y2="100" stroke="#FFD54F" stroke-width="0.8" opacity="0.6" class="central-connection"/>
              <line x1="600" y1="100" x2="582" y2="100" stroke="#FFD54F" stroke-width="0.8" opacity="0.6" class="central-connection"/>
              <line x1="600" y1="100" x2="600" y2="82" stroke="#FFD54F" stroke-width="0.8" opacity="0.6" class="central-connection"/>
              <line x1="600" y1="100" x2="600" y2="118" stroke="#FFD54F" stroke-width="0.8" opacity="0.6" class="central-connection"/>
              
              <!-- Cross connections between core nodes -->
              <line x1="618" y1="100" x2="600" y2="82" stroke="#FFF176" stroke-width="0.4" opacity="0.4"/>
              <line x1="582" y1="100" x2="600" y2="118" stroke="#FFEE58" stroke-width="0.4" opacity="0.4"/>
              <line x1="618" y1="100" x2="600" y2="118" stroke="#FFF176" stroke-width="0.3" opacity="0.3"/>
              <line x1="582" y1="100" x2="600" y2="82" stroke="#FFEE58" stroke-width="0.3" opacity="0.3"/>
            </g>
            
            <!-- Secondary micro-nodes layer -->
            <g>
              <animateTransform attributeName="transform" type="rotate" values="0 600 100;-360 600 100" dur="5s" repeatCount="indefinite"/>
              <circle cx="625" cy="100" r="1.5" fill="#FFCA28" opacity="0.5"/>
              <circle cx="575" cy="100" r="1.3" fill="#FFB300" opacity="0.5"/>
              <circle cx="600" cy="75" r="1.4" fill="#FF8F00" opacity="0.4"/>
              <circle cx="600" cy="125" r="1.6" fill="#FF6F00" opacity="0.4"/>
              <circle cx="612" cy="88" r="1.1" fill="#FFAB00" opacity="0.4"/>
              <circle cx="588" cy="112" r="1.2" fill="#FFC107" opacity="0.4"/>
              
              <!-- Secondary connections -->
              <line x1="600" y1="100" x2="625" y2="100" stroke="#FFCA28" stroke-width="0.3" opacity="0.3"/>
              <line x1="600" y1="100" x2="575" y2="100" stroke="#FFB300" stroke-width="0.3" opacity="0.3"/>
              <line x1="600" y1="100" x2="600" y2="75" stroke="#FF8F00" stroke-width="0.3" opacity="0.3"/>
              <line x1="600" y1="100" x2="600" y2="125" stroke="#FF6F00" stroke-width="0.3" opacity="0.3"/>
              <line x1="600" y1="100" x2="612" y2="88" stroke="#FFAB00" stroke-width="0.25" opacity="0.25"/>
              <line x1="600" y1="100" x2="588" y2="112" stroke="#FFC107" stroke-width="0.25" opacity="0.25"/>
            </g>
          </g>
          
          <!-- Inner Orbit Planets -->
          <g class="inner-orbit">
            <!-- Connection lines from center to inner planets -->
            <line x1="600" y1="100" x2="750" y2="100" stroke="#81C784" stroke-width="1" opacity="0.6" stroke-dasharray="3,2" class="central-connection"/>
            <line x1="600" y1="100" x2="450" y2="100" stroke="#81C784" stroke-width="1" opacity="0.6" stroke-dasharray="3,2" class="central-connection"/>
            <!-- Inter-planet connection -->
            <line x1="750" y1="100" x2="450" y2="100" stroke="#A5D6A7" stroke-width="0.8" opacity="0.4" stroke-dasharray="2,3" class="connection-line"/>
            
            <animateTransform attributeName="transform" type="rotate" values="0 600 100;360 600 100" dur="15s" repeatCount="indefinite"/>
            
            <!-- Main Planet 1 with micro-nodes -->
            <g>
              <circle cx="750" cy="100" r="6" fill="#4CAF50" opacity="0.5">
                <animate attributeName="r" values="6;8;6" dur="4s" repeatCount="indefinite"/>
              </circle>
              
              <!-- Micro-nodes around Planet 1 -->
              <g>
                <animateTransform attributeName="transform" type="rotate" values="0 750 100;360 750 100" dur="6s" repeatCount="indefinite"/>
                <circle cx="760" cy="100" r="1.5" fill="#66BB6A" opacity="0.7"/>
                <circle cx="740" cy="100" r="1" fill="#81C784" opacity="0.6"/>
                <circle cx="750" cy="92" r="1.2" fill="#A5D6A7" opacity="0.5"/>
                <circle cx="750" cy="108" r="1" fill="#C8E6C9" opacity="0.5"/>
                
                <!-- Micro-node connections -->
                <line x1="750" y1="100" x2="760" y2="100" stroke="#4CAF50" stroke-width="0.3" opacity="0.4"/>
                <line x1="750" y1="100" x2="740" y2="100" stroke="#4CAF50" stroke-width="0.3" opacity="0.4"/>
                <line x1="750" y1="100" x2="750" y2="92" stroke="#4CAF50" stroke-width="0.3" opacity="0.4"/>
                <line x1="750" y1="100" x2="750" y2="108" stroke="#4CAF50" stroke-width="0.3" opacity="0.4"/>
                <line x1="760" y1="100" x2="750" y2="92" stroke="#66BB6A" stroke-width="0.2" opacity="0.3"/>
                <line x1="740" y1="100" x2="750" y2="108" stroke="#81C784" stroke-width="0.2" opacity="0.3"/>
              </g>
            </g>
            
            <!-- Main Planet 2 with micro-nodes -->
            <g>
              <circle cx="450" cy="100" r="5" fill="#2196F3" opacity="0.5">
                <animate attributeName="r" values="5;7;5" dur="3.5s" repeatCount="indefinite"/>
              </circle>
              
              <!-- Micro-nodes around Planet 2 -->
              <g>
                <animateTransform attributeName="transform" type="rotate" values="0 450 100;-360 450 100" dur="5s" repeatCount="indefinite"/>
                <circle cx="458" cy="100" r="1.2" fill="#42A5F5" opacity="0.7"/>
                <circle cx="442" cy="100" r="1" fill="#64B5F6" opacity="0.6"/>
                <circle cx="450" cy="93" r="1" fill="#90CAF9" opacity="0.5"/>
                <circle cx="450" cy="107" r="1.3" fill="#BBDEFB" opacity="0.5"/>
                <circle cx="455" cy="95" r="0.8" fill="#E3F2FD" opacity="0.4"/>
                
                <!-- Micro-node connections -->
                <line x1="450" y1="100" x2="458" y2="100" stroke="#2196F3" stroke-width="0.3" opacity="0.4"/>
                <line x1="450" y1="100" x2="442" y2="100" stroke="#2196F3" stroke-width="0.3" opacity="0.4"/>
                <line x1="450" y1="100" x2="450" y2="93" stroke="#2196F3" stroke-width="0.3" opacity="0.4"/>
                <line x1="450" y1="100" x2="450" y2="107" stroke="#2196F3" stroke-width="0.3" opacity="0.4"/>
                <line x1="450" y1="100" x2="455" y2="95" stroke="#2196F3" stroke-width="0.2" opacity="0.3"/>
                <line x1="458" y1="100" x2="450" y2="93" stroke="#42A5F5" stroke-width="0.2" opacity="0.3"/>
                <line x1="442" y1="100" x2="450" y2="107" stroke="#64B5F6" stroke-width="0.2" opacity="0.3"/>
                <line x1="455" y1="95" x2="458" y2="100" stroke="#90CAF9" stroke-width="0.15" opacity="0.25"/>
              </g>
            </g>
          </g>
          
          <!-- Middle Orbit Planets -->
          <g class="middle-orbit">
            <!-- Connection lines from center to middle planets -->
            <line x1="600" y1="100" x2="850" y2="100" stroke="#81C784" stroke-width="0.8" opacity="0.5" stroke-dasharray="3,3" class="central-connection"/>
            <line x1="600" y1="100" x2="350" y2="100" stroke="#81C784" stroke-width="0.8" opacity="0.5" stroke-dasharray="3,3" class="central-connection"/>
            <line x1="600" y1="100" x2="600" y2="40" stroke="#81C784" stroke-width="0.8" opacity="0.5" stroke-dasharray="3,3" class="central-connection"/>
            <line x1="600" y1="100" x2="600" y2="160" stroke="#81C784" stroke-width="0.8" opacity="0.5" stroke-dasharray="3,3" class="central-connection"/>
            
            <!-- Inter-planet connections within middle orbit -->
            <line x1="850" y1="100" x2="350" y2="100" stroke="#90CAF9" stroke-width="0.7" opacity="0.35" stroke-dasharray="2,4" class="connection-line"/>
            <line x1="600" y1="40" x2="600" y2="160" stroke="#90CAF9" stroke-width="0.7" opacity="0.35" stroke-dasharray="2,4" class="connection-line"/>
            <line x1="850" y1="100" x2="600" y2="40" stroke="#A5D6A7" stroke-width="0.4" opacity="0.2" stroke-dasharray="1,5" class="connection-line"/>
            <line x1="850" y1="100" x2="600" y2="160" stroke="#A5D6A7" stroke-width="0.4" opacity="0.2" stroke-dasharray="1,5" class="connection-line"/>
            <line x1="350" y1="100" x2="600" y2="40" stroke="#A5D6A7" stroke-width="0.4" opacity="0.2" stroke-dasharray="1,5" class="connection-line"/>
            <line x1="350" y1="100" x2="600" y2="160" stroke="#A5D6A7" stroke-width="0.4" opacity="0.2" stroke-dasharray="1,5" class="connection-line"/>
            
            <animateTransform attributeName="transform" type="rotate" values="0 600 100;360 600 100" dur="25s" repeatCount="indefinite"/>
            
            <!-- Main Planet 3 with micro-nodes -->
            <g>
              <circle cx="850" cy="100" r="8" fill="#FF9800" opacity="0.4">
                <animate attributeName="r" values="8;10;8" dur="5s" repeatCount="indefinite"/>
              </circle>
              
              <!-- Micro-nodes around Planet 3 -->
              <g>
                <animateTransform attributeName="transform" type="rotate" values="0 850 100;360 850 100" dur="4s" repeatCount="indefinite"/>
                <circle cx="862" cy="100" r="1.8" fill="#FFB74D" opacity="0.6"/>
                <circle cx="838" cy="100" r="1.5" fill="#FFCC02" opacity="0.5"/>
                <circle cx="850" cy="88" r="1.3" fill="#FFE082" opacity="0.5"/>
                <circle cx="850" cy="112" r="1.6" fill="#FFF3E0" opacity="0.4"/>
                <circle cx="856" cy="92" r="1" fill="#FFAB40" opacity="0.5"/>
                <circle cx="844" cy="108" r="1.2" fill="#FF8A65" opacity="0.4"/>
                
                <!-- Micro-node connections -->
                <line x1="850" y1="100" x2="862" y2="100" stroke="#FF9800" stroke-width="0.4" opacity="0.4"/>
                <line x1="850" y1="100" x2="838" y2="100" stroke="#FF9800" stroke-width="0.4" opacity="0.4"/>
                <line x1="850" y1="100" x2="850" y2="88" stroke="#FF9800" stroke-width="0.4" opacity="0.4"/>
                <line x1="850" y1="100" x2="850" y2="112" stroke="#FF9800" stroke-width="0.4" opacity="0.4"/>
                <line x1="850" y1="100" x2="856" y2="92" stroke="#FF9800" stroke-width="0.3" opacity="0.3"/>
                <line x1="850" y1="100" x2="844" y2="108" stroke="#FF9800" stroke-width="0.3" opacity="0.3"/>
                <line x1="862" y1="100" x2="856" y2="92" stroke="#FFB74D" stroke-width="0.2" opacity="0.25"/>
                <line x1="838" y1="100" x2="844" y2="108" stroke="#FFCC02" stroke-width="0.2" opacity="0.25"/>
                <line x1="850" y1="88" x2="856" y2="92" stroke="#FFE082" stroke-width="0.15" opacity="0.2"/>
              </g>
            </g>
            
            <!-- Main Planet 4 with micro-nodes -->
            <g>
              <circle cx="350" cy="100" r="7" fill="#9C27B0" opacity="0.4">
                <animate attributeName="r" values="7;9;7" dur="4.5s" repeatCount="indefinite"/>
              </circle>
              
              <!-- Micro-nodes around Planet 4 -->
              <g>
                <animateTransform attributeName="transform" type="rotate" values="0 350 100;-360 350 100" dur="7s" repeatCount="indefinite"/>
                <circle cx="361" cy="100" r="1.4" fill="#BA68C8" opacity="0.6"/>
                <circle cx="339" cy="100" r="1.2" fill="#CE93D8" opacity="0.5"/>
                <circle cx="350" cy="89" r="1.1" fill="#E1BEE7" opacity="0.5"/>
                <circle cx="350" cy="111" r="1.3" fill="#F3E5F5" opacity="0.4"/>
                <circle cx="357" cy="94" r="0.9" fill="#AB47BC" opacity="0.5"/>
                
                <!-- Micro-node connections -->
                <line x1="350" y1="100" x2="361" y2="100" stroke="#9C27B0" stroke-width="0.3" opacity="0.4"/>
                <line x1="350" y1="100" x2="339" y2="100" stroke="#9C27B0" stroke-width="0.3" opacity="0.4"/>
                <line x1="350" y1="100" x2="350" y2="89" stroke="#9C27B0" stroke-width="0.3" opacity="0.4"/>
                <line x1="350" y1="100" x2="350" y2="111" stroke="#9C27B0" stroke-width="0.3" opacity="0.4"/>
                <line x1="350" y1="100" x2="357" y2="94" stroke="#9C27B0" stroke-width="0.2" opacity="0.3"/>
                <line x1="361" y1="100" x2="357" y2="94" stroke="#BA68C8" stroke-width="0.2" opacity="0.25"/>
                <line x1="339" y1="100" x2="350" y2="111" stroke="#CE93D8" stroke-width="0.2" opacity="0.25"/>
              </g>
            </g>
            
            <!-- Main Planet 5 with micro-nodes -->
            <g>
              <circle cx="600" cy="40" r="6" fill="#607D8B" opacity="0.4">
                <animate attributeName="r" values="6;8;6" dur="4.2s" repeatCount="indefinite"/>
              </circle>
              
              <!-- Micro-nodes around Planet 5 -->
              <g>
                <animateTransform attributeName="transform" type="rotate" values="0 600 40;360 600 40" dur="5.5s" repeatCount="indefinite"/>
                <circle cx="608" cy="40" r="1.1" fill="#78909C" opacity="0.5"/>
                <circle cx="592" cy="40" r="1" fill="#90A4AE" opacity="0.5"/>
                <circle cx="600" cy="32" r="0.9" fill="#B0BEC5" opacity="0.4"/>
                <circle cx="600" cy="48" r="1.2" fill="#CFD8DC" opacity="0.4"/>
                
                <!-- Micro-node connections -->
                <line x1="600" y1="40" x2="608" y2="40" stroke="#607D8B" stroke-width="0.25" opacity="0.3"/>
                <line x1="600" y1="40" x2="592" y2="40" stroke="#607D8B" stroke-width="0.25" opacity="0.3"/>
                <line x1="600" y1="40" x2="600" y2="32" stroke="#607D8B" stroke-width="0.25" opacity="0.3"/>
                <line x1="600" y1="40" x2="600" y2="48" stroke="#607D8B" stroke-width="0.25" opacity="0.3"/>
                <line x1="608" y1="40" x2="600" y2="32" stroke="#78909C" stroke-width="0.15" opacity="0.2"/>
                <line x1="592" y1="40" x2="600" y2="48" stroke="#90A4AE" stroke-width="0.15" opacity="0.2"/>
              </g>
            </g>
            
            <!-- Main Planet 6 with micro-nodes -->
            <g>
              <circle cx="600" cy="160" r="5" fill="#795548" opacity="0.4">
                <animate attributeName="r" values="5;7;5" dur="3.8s" repeatCount="indefinite"/>
              </circle>
              
              <!-- Micro-nodes around Planet 6 -->
              <g>
                <animateTransform attributeName="transform" type="rotate" values="0 600 160;-360 600 160" dur="6.5s" repeatCount="indefinite"/>
                <circle cx="607" cy="160" r="1" fill="#8D6E63" opacity="0.5"/>
                <circle cx="593" cy="160" r="1.1" fill="#A1887F" opacity="0.5"/>
                <circle cx="600" cy="153" r="0.8" fill="#BCAAA4" opacity="0.4"/>
                <circle cx="600" cy="167" r="1.2" fill="#D7CCC8" opacity="0.4"/>
                <circle cx="605" cy="155" r="0.9" fill="#6D4C41" opacity="0.4"/>
                
                <!-- Micro-node connections -->
                <line x1="600" y1="160" x2="607" y2="160" stroke="#795548" stroke-width="0.25" opacity="0.3"/>
                <line x1="600" y1="160" x2="593" y2="160" stroke="#795548" stroke-width="0.25" opacity="0.3"/>
                <line x1="600" y1="160" x2="600" y2="153" stroke="#795548" stroke-width="0.25" opacity="0.3"/>
                <line x1="600" y1="160" x2="600" y2="167" stroke="#795548" stroke-width="0.25" opacity="0.3"/>
                <line x1="600" y1="160" x2="605" y2="155" stroke="#795548" stroke-width="0.2" opacity="0.25"/>
                <line x1="607" y1="160" x2="605" y2="155" stroke="#8D6E63" stroke-width="0.15" opacity="0.2"/>
                <line x1="593" y1="160" x2="600" y2="167" stroke="#A1887F" stroke-width="0.15" opacity="0.2"/>
              </g>
            </g>
          </g>
          
          <!-- Outer Orbit Planets -->
          <g class="outer-orbit">
            <!-- Connection lines from center to outer planets -->
            <line x1="600" y1="100" x2="950" y2="100" stroke="#81C784" stroke-width="0.6" opacity="0.4" stroke-dasharray="2,4" class="central-connection"/>
            <line x1="600" y1="100" x2="250" y2="100" stroke="#81C784" stroke-width="0.6" opacity="0.4" stroke-dasharray="2,4" class="central-connection"/>
            
            <!-- Inter-planet connection in outer orbit -->
            <line x1="950" y1="100" x2="250" y2="100" stroke="#FFCC80" stroke-width="0.6" opacity="0.3" stroke-dasharray="2,5" class="connection-line"/>
            
            <animateTransform attributeName="transform" type="rotate" values="0 600 100;360 600 100" dur="40s" repeatCount="indefinite"/>
            
            <!-- Main Planet 7 with micro-nodes -->
            <g>
              <circle cx="950" cy="100" r="9" fill="#FF5722" opacity="0.35">
                <animate attributeName="r" values="9;11;9" dur="6s" repeatCount="indefinite"/>
              </circle>
              
              <!-- Micro-nodes around Planet 7 -->
              <g>
                <animateTransform attributeName="transform" type="rotate" values="0 950 100;360 950 100" dur="8s" repeatCount="indefinite"/>
                <circle cx="964" cy="100" r="2" fill="#FF7043" opacity="0.5"/>
                <circle cx="936" cy="100" r="1.8" fill="#FF8A65" opacity="0.4"/>
                <circle cx="950" cy="86" r="1.5" fill="#FFAB91" opacity="0.4"/>
                <circle cx="950" cy="114" r="1.7" fill="#FFCCBC" opacity="0.3"/>
                <circle cx="958" cy="90" r="1.3" fill="#D84315" opacity="0.4"/>
                <circle cx="942" cy="110" r="1.4" fill="#BF360C" opacity="0.3"/>
                <circle cx="945" cy="95" r="1.1" fill="#FF6E40" opacity="0.3"/>
                
                <!-- Micro-node connections -->
                <line x1="950" y1="100" x2="964" y2="100" stroke="#FF5722" stroke-width="0.5" opacity="0.3"/>
                <line x1="950" y1="100" x2="936" y2="100" stroke="#FF5722" stroke-width="0.5" opacity="0.3"/>
                <line x1="950" y1="100" x2="950" y2="86" stroke="#FF5722" stroke-width="0.5" opacity="0.3"/>
                <line x1="950" y1="100" x2="950" y2="114" stroke="#FF5722" stroke-width="0.5" opacity="0.3"/>
                <line x1="950" y1="100" x2="958" y2="90" stroke="#FF5722" stroke-width="0.4" opacity="0.25"/>
                <line x1="950" y1="100" x2="942" y2="110" stroke="#FF5722" stroke-width="0.4" opacity="0.25"/>
                <line x1="950" y1="100" x2="945" y2="95" stroke="#FF5722" stroke-width="0.3" opacity="0.2"/>
                <line x1="964" y1="100" x2="958" y2="90" stroke="#FF7043" stroke-width="0.2" opacity="0.2"/>
                <line x1="936" y1="100" x2="942" y2="110" stroke="#FF8A65" stroke-width="0.2" opacity="0.2"/>
                <line x1="958" y1="90" x2="945" y2="95" stroke="#D84315" stroke-width="0.15" opacity="0.15"/>
              </g>
            </g>
            
            <!-- Main Planet 8 with micro-nodes -->
            <g>
              <circle cx="250" cy="100" r="8" fill="#3F51B5" opacity="0.35">
                <animate attributeName="r" values="8;10;8" dur="5.5s" repeatCount="indefinite"/>
              </circle>
              
              <!-- Micro-nodes around Planet 8 -->
              <g>
                <animateTransform attributeName="transform" type="rotate" values="0 250 100;-360 250 100" dur="9s" repeatCount="indefinite"/>
                <circle cx="263" cy="100" r="1.9" fill="#5C6BC0" opacity="0.5"/>
                <circle cx="237" cy="100" r="1.6" fill="#7986CB" opacity="0.4"/>
                <circle cx="250" cy="87" r="1.4" fill="#9FA8DA" opacity="0.4"/>
                <circle cx="250" cy="113" r="1.8" fill="#C5CAE9" opacity="0.3"/>
                <circle cx="257" cy="91" r="1.2" fill="#303F9F" opacity="0.4"/>
                <circle cx="243" cy="109" r="1.5" fill="#1A237E" opacity="0.3"/>
                <circle cx="255" cy="107" r="1" fill="#536DFE" opacity="0.3"/>
                <circle cx="245" cy="93" r="1.3" fill="#448AFF" opacity="0.25"/>
                
                <!-- Micro-node connections -->
                <line x1="250" y1="100" x2="263" y2="100" stroke="#3F51B5" stroke-width="0.5" opacity="0.3"/>
                <line x1="250" y1="100" x2="237" y2="100" stroke="#3F51B5" stroke-width="0.5" opacity="0.3"/>
                <line x1="250" y1="100" x2="250" y2="87" stroke="#3F51B5" stroke-width="0.5" opacity="0.3"/>
                <line x1="250" y1="100" x2="250" y2="113" stroke="#3F51B5" stroke-width="0.5" opacity="0.3"/>
                <line x1="250" y1="100" x2="257" y2="91" stroke="#3F51B5" stroke-width="0.4" opacity="0.25"/>
                <line x1="250" y1="100" x2="243" y2="109" stroke="#3F51B5" stroke-width="0.4" opacity="0.25"/>
                <line x1="250" y1="100" x2="255" y2="107" stroke="#3F51B5" stroke-width="0.3" opacity="0.2"/>
                <line x1="250" y1="100" x2="245" y2="93" stroke="#3F51B5" stroke-width="0.3" opacity="0.2"/>
                <line x1="263" y1="100" x2="257" y2="91" stroke="#5C6BC0" stroke-width="0.2" opacity="0.2"/>
                <line x1="237" y1="100" x2="243" y2="109" stroke="#7986CB" stroke-width="0.2" opacity="0.2"/>
                <line x1="255" y1="107" x2="245" y2="93" stroke="#536DFE" stroke-width="0.15" opacity="0.15"/>
              </g>
            </g>
          </g>
          
          <!-- Cross-Orbit Network Connections -->
          <!-- Inner to Middle Orbit Connections -->
          <g class="cross-orbit-inner-middle">
            <animateTransform attributeName="transform" type="rotate" values="0 600 100;360 600 100" dur="20s" repeatCount="indefinite"/>
            <line x1="750" y1="100" x2="850" y2="100" stroke="#A5D6A7" stroke-width="0.5" opacity="0.25" stroke-dasharray="1,4" class="connection-line"/>
            <line x1="450" y1="100" x2="350" y2="100" stroke="#A5D6A7" stroke-width="0.5" opacity="0.25" stroke-dasharray="1,4" class="connection-line"/>
            <line x1="750" y1="100" x2="600" y2="40" stroke="#A5D6A7" stroke-width="0.4" opacity="0.2" stroke-dasharray="1,5" class="connection-line"/>
            <line x1="450" y1="100" x2="600" y2="160" stroke="#A5D6A7" stroke-width="0.4" opacity="0.2" stroke-dasharray="1,5" class="connection-line"/>
          </g>
          
          <!-- Middle to Outer Orbit Connections -->
          <g class="cross-orbit-middle-outer">
            <animateTransform attributeName="transform" type="rotate" values="0 600 100;360 600 100" dur="30s" repeatCount="indefinite"/>
            <line x1="850" y1="100" x2="950" y2="100" stroke="#90CAF9" stroke-width="0.4" opacity="0.2" stroke-dasharray="1,5" class="connection-line"/>
            <line x1="350" y1="100" x2="250" y2="100" stroke="#90CAF9" stroke-width="0.4" opacity="0.2" stroke-dasharray="1,5" class="connection-line"/>
          </g>
          
          <!-- Inner to Outer Orbit Long Connections -->
          <g class="cross-orbit-inner-outer">
            <animateTransform attributeName="transform" type="rotate" values="0 600 100;360 600 100" dur="35s" repeatCount="indefinite"/>
            <line x1="750" y1="100" x2="950" y2="100" stroke="#FFCC80" stroke-width="0.3" opacity="0.15" stroke-dasharray="1,8" class="connection-line"/>
            <line x1="450" y1="100" x2="250" y2="100" stroke="#FFCC80" stroke-width="0.3" opacity="0.15" stroke-dasharray="1,8" class="connection-line"/>
          </g>
          
          <!-- Asteroid Belt (Small dots) -->
          <g class="asteroid-belt">
            <animateTransform attributeName="transform" type="rotate" values="0 600 100;360 600 100" dur="60s" repeatCount="indefinite"/>
            <circle cx="800" cy="85" r="2" fill="#BDBDBD" opacity="0.3"/>
            <circle cx="820" cy="115" r="1.5" fill="#9E9E9E" opacity="0.25"/>
            <circle cx="780" cy="120" r="2.5" fill="#757575" opacity="0.2"/>
            <circle cx="400" cy="85" r="2" fill="#BDBDBD" opacity="0.3"/>
            <circle cx="380" cy="115" r="1.5" fill="#9E9E9E" opacity="0.25"/>
            <circle cx="420" cy="120" r="2.5" fill="#757575" opacity="0.2"/>
            <circle cx="600" cy="65" r="1.5" fill="#BDBDBD" opacity="0.25"/>
            <circle cx="580" cy="135" r="2" fill="#9E9E9E" opacity="0.2"/>
            <circle cx="620" cy="130" r="1.5" fill="#757575" opacity="0.25"/>
          </g>
          
          <!-- Distant Stars -->
          <circle cx="100" cy="30" r="1" fill="#FFD54F" opacity="0.2">
            <animate attributeName="opacity" values="0.1;0.3;0.1" dur="5s" repeatCount="indefinite"/>
          </circle>
          <circle cx="1100" cy="40" r="1.5" fill="#FFEB3B" opacity="0.15">
            <animate attributeName="opacity" values="0.1;0.25;0.1" dur="7s" repeatCount="indefinite"/>
          </circle>
          <circle cx="50" cy="160" r="1" fill="#FFC107" opacity="0.2">
            <animate attributeName="opacity" values="0.05;0.2;0.05" dur="6s" repeatCount="indefinite"/>
          </circle>
          <circle cx="1150" cy="170" r="1.2" fill="#FFD54F" opacity="0.18">
            <animate attributeName="opacity" values="0.1;0.25;0.1" dur="4s" repeatCount="indefinite"/>
          </circle>

          <!-- Subtle Energy Waves from Sun -->
          <circle cx="600" cy="100" r="25" fill="none" stroke="#FFD54F" stroke-width="0.5" opacity="0.1">
            <animate attributeName="r" values="25;80;25" dur="12s" repeatCount="indefinite"/>
            <animate attributeName="opacity" values="0.1;0.05;0.1" dur="12s" repeatCount="indefinite"/>
          </circle>
          <circle cx="600" cy="100" r="35" fill="none" stroke="#FFEB3B" stroke-width="0.5" opacity="0.08">
            <animate attributeName="r" values="35;120;35" dur="18s" repeatCount="indefinite"/>
            <animate attributeName="opacity" values="0.08;0.03;0.08" dur="18s" repeatCount="indefinite"/>
          </circle>
          <circle cx="600" cy="100" r="45" fill="none" stroke="#FFC107" stroke-width="0.5" opacity="0.06">
            <animate attributeName="r" values="45;160;45" dur="25s" repeatCount="indefinite"/>
            <animate attributeName="opacity" values="0.06;0.02;0.06" dur="25s" repeatCount="indefinite"/>
          </circle>
        </svg>
      </div>
      
      <div :class="headerContainerClasses">
        <!-- Desktop Layout (3 columns) -->
        <div class="hidden md:grid grid-cols-3 items-center min-h-32">
          <!-- Left Column - Old Logo -->
          <div class="flex justify-start">
            <img 
              :src="clusterLogo" 
              :alt="clusterAltText" 
              class="h-32 w-auto max-w-xs"
            >
          </div>
          
          <!-- Center Column - New Logo -->
          <div class="flex justify-center">
            <img 
              src="https://vue-project.sgp1.digitaloceanspaces.com/2025/10/1761639129428.png"
              alt="โลโก้ใหม่ Clustersie"
              class="h-24 w-auto object-contain"
            >
          </div>
          
          <!-- Right Column - Text Content -->
          <div class="text-right">
            <h1 class="text-4xl font-bold text-gray-900 mb-2">{{ clusterTitle }}</h1>
            <h2 class="text-lg text-gray-600 font-medium">{{ clusterSubtitle }}</h2>
          </div>
        </div>

        <!-- Mobile Layout (2 columns, 1 row) -->
        <div class="md:hidden">
          <div class="grid grid-cols-2 items-center gap-4 mb-4">
            <!-- Left Column - Old Logo -->
            <div class="flex justify-start">
              <img 
                :src="clusterLogo" 
                :alt="clusterAltText" 
                class="h-16 w-auto object-contain"
              >
            </div>
            
            <!-- Right Column - New Logo (smaller) -->
            <div class="flex justify-end">
              <img 
                src="https://vue-project.sgp1.digitaloceanspaces.com/2025/10/1761639129428.png"
                alt="โลโก้ใหม่ Clustersie"
                class="h-12 w-auto object-contain"
              >
            </div>
          </div>
          
          <!-- Text Content (centered below logos) -->
          <div class="text-center">
            <h1 class="text-2xl font-bold text-gray-900 mb-2">{{ clusterTitle }}</h1>
            <h2 class="text-base text-gray-600 font-medium px-4">{{ clusterSubtitle }}</h2>
          </div>
        </div>
      </div>
    </header>

    

    <!-- Main Navigation -->
    <nav 
      v-if="mode !== 'pages'"
      :class="navClasses"
      class="nav-sticky shadow-lg transition-all duration-300"
      :style="navStyles"
    >
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <!-- Desktop Menu -->
        <div class="hidden md:flex justify-between items-center">
          <!-- Left Side - Main Menu -->
          <div class="flex space-x-8">
            <a @click="smoothScrollTo('news', $event)" href="#news" :class="getNavItemClasses('news')" class="nav-item text-white px-3 py-4 text-sm font-medium transition-all duration-200 hover:scale-105 cursor-pointer">ข่าวสาร</a>
            <a @click="smoothScrollTo('home', $event)" href="#home" :class="getNavItemClasses('home')" class="nav-item text-white px-3 py-4 text-sm font-medium transition-all duration-200 hover:scale-105 cursor-pointer">หน้าแรก</a>
            <a v-if="currentSite === 'main'" @click="smoothScrollTo('dashboard', $event)" href="#dashboard" :class="getNavItemClasses('dashboard')" class="nav-item text-white px-3 py-4 text-sm font-medium transition-all duration-200 hover:scale-105 cursor-pointer">แดชบอร์ด</a>
            <a v-if="currentSite === 'main'" @click="navigateToHomePage" :class="navHoverClasses" class="nav-item text-white px-3 py-4 text-sm font-medium transition-all duration-200 hover:scale-105 cursor-pointer">หน้าเว็บ</a>
            <a v-if="currentSite === 'main'" @click="smoothScrollTo('clusters', $event)" href="#clusters" :class="getNavItemClasses('clusters')" class="nav-item text-white px-3 py-4 text-sm font-medium transition-all duration-200 hover:scale-105 cursor-pointer">คลัสเตอร์</a>
            <a @click="smoothScrollTo('products', $event)" href="#products" :class="getNavItemClasses('products')" class="nav-item text-white px-3 py-4 text-sm font-medium transition-all duration-200 hover:scale-105 cursor-pointer">สินค้า</a>
            <a @click="smoothScrollTo('articles', $event)" href="#articles" :class="getNavItemClasses('articles')" class="nav-item text-white px-3 py-4 text-sm font-medium transition-all duration-200 hover:scale-105 cursor-pointer">บทความ</a>
            <a @click="smoothScrollTo('gallery', $event)" href="#gallery" :class="getNavItemClasses('gallery')" class="nav-item text-white px-3 py-4 text-sm font-medium transition-all duration-200 hover:scale-105 cursor-pointer">แกลเลอรี่</a>
            <a @click="openContactModal" :class="navHoverClasses" class="nav-item text-white px-3 py-4 text-sm font-medium transition-all duration-200 hover:scale-105 cursor-pointer">ติดต่อ</a>
          </div>

          <!-- Right Side - Member Menu -->
          <div class="flex items-center space-x-4">
            <!-- Debug info for scroll state -->
            <span v-if="isScrolled" class="text-xs text-green-200 hidden lg:block">
              📍 {{ scrollPosition }}px
            </span>
            
            <!-- Login Button (เฉพาะเมื่อยังไม่ได้ login) -->
            <button v-if="!isLoggedIn" @click="openLoginModal" :class="loginButtonClasses" class="text-white px-3 py-2 text-sm font-medium transition-colors border border-white rounded-md">
              เข้าสู่ระบบ
            </button>
          </div>
        </div>

        <!-- Mobile Menu Button -->
        <div class="md:hidden flex items-center justify-end py-3">
          <button 
            @click="toggleMobileMenu" 
            :class="mobileMenuButtonClasses"
            class="text-white focus:outline-none transition-colors"
            aria-label="เปิด/ปิดเมนู"
          >
            <svg v-if="!isMobileMenuOpen" class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"></path>
            </svg>
            <svg v-else class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>

        <!-- Mobile Menu Panel -->
        <div v-show="isMobileMenuOpen" :class="mobileMenuBorderClasses" class="md:hidden border-t">
          <div :class="mobileMenuBgClasses" class="px-2 pt-2 pb-3 space-y-1">
            <a @click="smoothScrollTo('news', $event)" href="#news" :class="getMobileNavItemClasses('news')" class="block px-3 py-3 text-white rounded-md text-base font-medium transition-all duration-200 hover:scale-105 cursor-pointer">
              📰 ข่าวสาร
            </a>
            <a @click="smoothScrollTo('home', $event)" href="#home" :class="getMobileNavItemClasses('home')" class="block px-3 py-3 text-white rounded-md text-base font-medium transition-all duration-200 hover:scale-105 cursor-pointer">
              🏠 หน้าแรก
            </a>
            <a v-if="currentSite === 'main'" @click="smoothScrollTo('dashboard', $event)" href="#dashboard" :class="getMobileNavItemClasses('dashboard')" class="block px-3 py-3 text-white rounded-md text-base font-medium transition-all duration-200 hover:scale-105 cursor-pointer">
              📊 แดชบอร์ด
            </a>
            <a v-if="currentSite === 'main'" @click="smoothScrollTo('clusters', $event)" href="#clusters" :class="getMobileNavItemClasses('clusters')" class="block px-3 py-3 text-white rounded-md text-base font-medium transition-all duration-200 hover:scale-105 cursor-pointer">
              🌐 คลัสเตอร์
            </a>
            <a @click="smoothScrollTo('products', $event)" href="#products" :class="getMobileNavItemClasses('products')" class="block px-3 py-3 text-white hover:bg-green-700 hover:text-green-100 rounded-md text-base font-medium transition-all duration-200 hover:scale-105 cursor-pointer">
              �️ สินค้า
            </a>
            <a @click="smoothScrollTo('articles', $event)" href="#articles" :class="getMobileNavItemClasses('articles')" class="block px-3 py-3 text-white hover:bg-green-700 hover:text-green-100 rounded-md text-base font-medium transition-all duration-200 hover:scale-105 cursor-pointer">
              � บทความ
            </a>
            <a @click="smoothScrollTo('gallery', $event)" href="#gallery" :class="getMobileNavItemClasses('gallery')" class="block px-3 py-3 text-white hover:bg-green-700 hover:text-green-100 rounded-md text-base font-medium transition-all duration-200 hover:scale-105 cursor-pointer">
              � แกลเลอรี่
            </a>
            <a @click="openContactModal" class="block px-3 py-3 text-white hover:bg-green-700 hover:text-green-100 rounded-md text-base font-medium transition-all duration-200 hover:scale-105 cursor-pointer">
              📞 ติดต่อ
            </a>
            
            <!-- Member Menu for Mobile -->
            <div v-if="!isLoggedIn" :class="`border-t ${mobileMenuBorderClasses} mt-2 pt-2`">
              <!-- Login Button (เฉพาะเมื่อยังไม่ได้ login) -->
              <button @click="openLoginModal" :class="`block px-3 py-3 text-white ${mobileNavItemHoverClasses} rounded-md text-base font-medium transition-colors border border-white mx-2 mb-2 text-center w-full`">
                🔑 เข้าสู่ระบบ
              </button>
            </div>
          </div>
        </div>
      </div>
    </nav>


    <!-- User Bar (แสดงเมื่อ login แล้ว) -->
    <div 
      v-if="isLoggedIn && currentUser"
      class="bg-black text-white shadow-sm"
    >
      <div :class="userBarContainerClasses">
        <!-- Desktop Layout (1 row) -->
        <div class="hidden md:flex justify-between items-center py-2">
          <!-- Left Side - User Info -->
          <div class="flex items-center space-x-4">
            <div class="flex items-center space-x-2">
              <div class="w-8 h-8 bg-gray-600 rounded-full flex items-center justify-center">
                <span class="text-xs font-medium text-white">
                  {{ getUserInitials(currentUser) }}
                </span>
              </div>
              <div class="text-sm">
                <span class="font-medium">{{ currentUser.name || currentUser.email }}</span>
                <span v-if="currentUser.role" class="text-gray-300 ml-2">({{ currentUser.role }})</span>
              </div>
            </div>
            
            <!-- Active OM/VC Info -->
            <div v-if="activeOmInfo" class="text-xs text-gray-300 border-l border-gray-600 pl-4">
              <span>{{ activeOmInfo.name }}</span>
              <span v-if="activeVcInfo" class="ml-2">• {{ activeVcInfo.name }}</span>
            </div>
          </div>

          <!-- Right Side - Actions -->
          <div class="flex items-center space-x-3">
            
            <!-- Switch OM/VC Button -->
            <button 
              v-if="availableOmData.length > 0"
              @click="openOmVcSelection"
              class="text-xs bg-gray-700 hover:bg-gray-600 text-white px-3 py-1 rounded transition-colors"
            >
              เปลี่ยน OM/VC
            </button>
            
            <!-- Dashboard Link -->
            <NuxtLink 
              :to="currentSite === 'main' ? '/cluster' : '/cluster/user/dashboard'"
              class="text-xs bg-blue-600 hover:bg-blue-500 text-white px-3 py-1 rounded transition-colors"
            >
              แดชบอร์ด
            </NuxtLink>

            <!-- Website Link -->
            <NuxtLink 
              to="/"
              class="text-xs bg-blue-600 hover:bg-blue-500 text-white px-3 py-1 rounded transition-colors"
            >
              หน้าเว็บ
            </NuxtLink>
            
            <!-- Logout Button -->
            <button 
              @click="handleLogout"
              class="text-xs bg-red-600 hover:bg-red-500 text-white px-3 py-1 rounded transition-colors"
            >
              ออกจากระบบ
            </button>
          </div>
        </div>

        <!-- Mobile Layout (2 rows) -->
        <div class="md:hidden py-2 space-y-2">
          <!-- Row 1: User Info -->
          <div class="flex items-center justify-between">
            <div class="flex items-center space-x-2">
              <div class="w-8 h-8 bg-gray-600 rounded-full flex items-center justify-center">
                <span class="text-xs font-medium text-white">
                  {{ getUserInitials(currentUser) }}
                </span>
              </div>
              <div class="text-sm">
                <span class="font-medium">{{ currentUser.name || currentUser.email }}</span>
                <span v-if="currentUser.role" class="text-gray-300 ml-1 text-xs">({{ currentUser.role }})</span>
              </div>
            </div>
            
            <!-- Session Time (right aligned) -->
            <div v-if="sessionTime" class="text-xs text-gray-300">
              {{ sessionTime }}
            </div>
          </div>
          
          <!-- Row 2: OM/VC Info & Actions -->
          <div class="flex items-center justify-between space-x-2">
            <!-- Active OM/VC Info -->
            <div v-if="activeOmInfo" class="text-xs text-gray-300 flex-1 min-w-0">
              <div class="truncate">
                <span>{{ activeOmInfo.name }}</span>
                <span v-if="activeVcInfo" class="ml-1">• {{ activeVcInfo.name }}</span>
              </div>
            </div>
            
            <!-- Action Buttons -->
            <div class="flex items-center space-x-1 flex-shrink-0">
              <!-- Switch OM/VC Button -->
              <button 
                v-if="availableOmData.length > 0"
                @click="openOmVcSelection"
                class="text-xs bg-gray-700 hover:bg-gray-600 text-white px-2 py-1 rounded transition-colors"
              >
                เปลี่ยน
              </button>
              
              <!-- Dashboard Link -->
              <NuxtLink 
                to="/cluster/user/dashboard"
                class="text-xs bg-blue-600 hover:bg-blue-500 text-white px-2 py-1 rounded transition-colors"
              >
                แดช
              </NuxtLink>

              <!-- Website Link -->
              <NuxtLink 
                to="/"
                class="text-xs bg-blue-600 hover:bg-blue-500 text-white px-2 py-1 rounded transition-colors"
              >
                เว็บ
              </NuxtLink>
              
              <!-- Logout Button -->
              <button 
                @click="handleLogout"
                class="text-xs bg-red-600 hover:bg-red-500 text-white px-2 py-1 rounded transition-colors"
              >
                ออก
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Contact Modal -->
    <div v-if="isContactModalOpen" class="fixed inset-0 bg-black bg-opacity-40 z-[1100] flex items-center justify-center p-4" @click="closeContactModal">
      <div class="bg-white rounded-lg max-w-sm w-full shadow-lg" @click.stop>
        <!-- Modal Header -->
        <div class="flex items-center justify-between p-4 border-b">
          <h3 class="text-lg font-medium text-gray-900">ติดต่อเรา</h3>
          <button @click="closeContactModal" class="text-gray-400 hover:text-gray-600">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>

        <!-- QR Code Section -->
        <div class="p-4 text-center">
          <div class="bg-gray-50 rounded p-3 inline-block mb-3">
            <img 
              :src="`https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=${encodeURIComponent('ข้อมูลติดต่อ Clustersie\\nโทร: 02-123-4567\\nEmail: info@clustersie.com')}`" 
              alt="QR Code ข้อมูลติดต่อ" 
              class="w-30 h-30"
            />
          </div>
          <p class="text-sm text-gray-600">สแกนเพื่อบันทึกข้อมูลติดต่อ</p>
        </div>

        <!-- Contact Information -->
        <div class="px-4 pb-4 space-y-3 text-sm">
          <div class="flex items-center space-x-2">
            <span class="text-gray-500">📞</span>
            <span>02-123-4567</span>
          </div>
          <div class="flex items-center space-x-2">
            <span class="text-gray-500">📧</span>
            <span>info@clustersie.com</span>
          </div>
          <div class="flex items-start space-x-2">
            <span class="text-gray-500">📍</span>
            <span>123 ถนนสุขุมวิท แขวงคลองเตย กรุงเทพฯ 10110</span>
          </div>
          <div class="flex items-start space-x-2">
            <span class="text-gray-500">⏰</span>
            <span>จ-ศ: 9:00-18:00 | ส: 9:00-12:00</span>
          </div>
        </div>

        <!-- Modal Footer -->
        <div class="p-4 border-t">
          <button @click="closeContactModal" :class="contactModalButtonClasses">
            ปิด
          </button>
        </div>
      </div>
    </div>

    <!-- Login Modal -->
    <div v-if="isLoginModalOpen" class="fixed inset-0 bg-black bg-opacity-40 z-[1100] flex items-center justify-center p-4" @click="closeLoginModal">
      <div class="bg-white rounded-lg max-w-sm w-full shadow-lg" @click.stop>
        <!-- Modal Header -->
        <div class="flex items-center justify-between p-4 border-b">
          <h3 class="text-lg font-medium text-gray-900">เข้าสู่ระบบ</h3>
          <button @click="closeLoginModal" class="text-gray-400 hover:text-gray-600">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>

        <!-- Login Form -->
        <div class="p-4">
          <form @submit.prevent="handleLogin" class="space-y-4">
            <div>
              <label for="email" class="block text-sm font-medium text-gray-700 mb-1">
                {{ mode === 'root' ? 'ชื่อผู้ใช้' : 'อีเมล' }}
              </label>
              <input 
                :type="mode === 'root' ? 'text' : 'email'" 
                id="email" 
                v-model="currentLoginForm.email"
                :class="formInputClasses"
                :placeholder="mode === 'root' ? 'กรอกชื่อผู้ใช้ (admin)' : 'กรอกอีเมลของคุณ'"
                :required="mode !== 'root'"
              />
            </div>
            
            <div>
              <label for="password" class="block text-sm font-medium text-gray-700 mb-1">รหัสผ่าน</label>
              <input 
                type="password" 
                id="password" 
                v-model="currentLoginForm.password"
                :class="formInputClasses"
                :placeholder="mode === 'root' ? 'กรอกรหัสผ่าน (1234)' : 'กรอกรหัสผ่าน'"
                required
              />
            </div>

            <!-- Hardcode Login Hint for Root Mode -->
            <!-- <div v-if="mode === 'root'" class="bg-blue-50 border border-blue-200 rounded-md p-3 text-sm">
              <div class="flex items-center">
                <svg class="w-4 h-4 text-blue-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                <div>
                  <p class="text-blue-700 font-medium">ข้อมูลสำหรับทดสอบ:</p>
                  <p class="text-blue-600">ชื่อผู้ใช้: <span class="font-mono bg-blue-100 px-1 rounded">admin</span></p>
                  <p class="text-blue-600">รหัสผ่าน: <span class="font-mono bg-blue-100 px-1 rounded">1234</span></p>
                </div>
              </div>
            </div> -->

            <div v-if="loginErrorMessage" class="text-red-600 text-sm text-center pt-2">
              {{ loginErrorMessage }}
            </div>

            <div class="flex items-center justify-between text-sm">
              <label class="flex items-center">
                <input type="checkbox" v-model="currentLoginForm.remember" class="mr-2">
                จดจำการเข้าสู่ระบบ
              </label>
              <a href="#" :class="linkClasses">ลืมรหัสผ่าน?</a>
            </div>

            <button 
              type="submit" 
              :disabled="isLogging"
              :class="loginSubmitButtonClasses"
            >
              <span v-if="isLogging" class="flex items-center justify-center">
                <!-- Loading Spinner -->
                <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                กำลังดำเนินการ...
              </span>
              <span v-else>เข้าสู่ระบบ</span>
            </button>
          </form>
        </div>
      </div>
    </div>

    <!-- OM/VC Selection Modal -->
    <OMVCSelectionModal
      :visible="showSelectionModal"
      :om-data="availableOmData"
      @confirm="handleSelectionConfirmed"
      @cancel="handleSelectionCancelled"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useNuxtApp, useRouter } from '#app'
import SiteSelector from './SiteSelector.vue'
import MobileMenu from './MobileMenu.vue'
// Import OMVCSelectionModal component
import OMVCSelectionModal from '~/components/OMVCSelectionModal.vue'

// Define props
const props = defineProps({
  currentSite: {
    type: String,
    default: 'main'
  },
  activeSection: {
    type: String,
    default: 'home'
  },
  mode: {
    type: String,
    default: 'root'
  }
})

// Define emits for communication with parent
const emit = defineEmits(['toggleMobileMenu', 'smoothScrollTo', 'openContactModal', 'openLoginModal'])

// Initialize Nuxt composables
const { $DSS } = useNuxtApp()
const router = useRouter()

// State
const isMobileMenuOpen = ref(false)

// Scroll state (managed internally)
const isScrolled = ref(false)
const scrollPosition = ref(0)

// Contact modal state
const isContactModalOpen = ref(false)

// Login modal state
const isLoginModalOpen = ref(false)
const isLogging = ref(false)
const loginForm = ref({
  email: '',
  password: '',
  remember: false
})
const loginErrorMessage = ref('')

// Separate login forms for different modes
const pagesLoginForm = ref({
  email: '',
  password: '',
  remember: false
})
const rootLoginForm = ref({
  email: '',
  password: '',
  remember: false
})

// Computed property to get current form based on mode
const currentLoginForm = computed(() => {
  return props.mode === 'root' ? rootLoginForm.value : pagesLoginForm.value
})

// OM/VC Selection modal state
const showSelectionModal = ref(false)
const availableOmData = ref([])
let loginResponseData = null // เก็บข้อมูล response ชั่วคราว

// User authentication state
const isLoggedIn = ref(false)
const currentUser = ref(null)
const activeOmInfo = ref(null)
const activeVcInfo = ref(null)
const sessionTime = ref('')

// Methods
const toggleMobileMenu = () => {
  isMobileMenuOpen.value = !isMobileMenuOpen.value
  emit('toggleMobileMenu')
}

// Contact modal functions
const openContactModal = () => {
  console.log('Opening contact modal')
  isContactModalOpen.value = true
  closeMobileMenu()
  // Also emit to parent in case it needs to know
  emit('openContactModal')
}

const closeContactModal = () => {
  console.log('Closing contact modal')
  isContactModalOpen.value = false
}

// Login modal functions
const openLoginModal = () => {
  console.log('Opening login modal')
  isLoginModalOpen.value = true
  closeMobileMenu()
  // Also emit to parent in case it needs to know
  emit('openLoginModal')
}

const closeLoginModal = () => {
  isLoginModalOpen.value = false
  loginErrorMessage.value = ''
  
  // Reset appropriate form based on mode
  if (props.mode === 'root') {
    rootLoginForm.value = {
      email: '',
      password: '',
      remember: false
    }
  } else {
    pagesLoginForm.value = {
      email: '',
      password: '',
      remember: false
    }
  }
  
  // Reset old loginForm for backward compatibility
  loginForm.value = {
    email: '',
    password: '',
    remember: false
  }
}

const closeMobileMenu = () => {
  isMobileMenuOpen.value = false
}

// Handle login form submission
const handleLogin = async () => {
  isLogging.value = true
  loginErrorMessage.value = '' // Clear previous errors

  try {
    // ถ้าเป็น main site ให้ใช้ hardcode login
    if (props.currentSite === 'main') {
      console.log('Main site: Using hardcode login')
      
      // ตรวจสอบ hardcode credentials
      if (currentLoginForm.value.email === 'admin' && currentLoginForm.value.password === '1234') {
        // Login สำเร็จแบบ hardcode
        console.log('Hardcode login successful')
        
        // สร้าง response data แบบ mock เหมือนระบบปกติ
        const mockResponseData = {
          user: {
            id: 1,
            name: 'Admin User',
            email: 'admin',
            role: 'Administrator',
            avatar: null,
            permissions: ['admin', 'read', 'write', 'delete'],
            lastLogin: new Date().toISOString()
          },
          cluster: {
            id: 1,
            name: 'Main Cluster',
            type: 'main',
            description: 'หลักระบบคลัสเตอร์หลัก',
            status: 'active'
          },
          omData: [
            {
              id: 1,
              name: 'Organization Main',
              code: 'ORG001',
              type: 'headquarters',
              vcData: [
                {
                  id: 1,
                  name: 'Value Chain Primary',
                  code: 'VC001',
                  type: 'primary'
                },
                {
                  id: 2,
                  name: 'Value Chain Secondary', 
                  code: 'VC002',
                  type: 'secondary'
                }
              ]
            },
            {
              id: 2,
              name: 'Organization Branch',
              code: 'ORG002',
              type: 'branch',
              vcData: [
                {
                  id: 3,
                  name: 'Value Chain Branch',
                  code: 'VC003',
                  type: 'branch'
                }
              ]
            }
          ],
          token: 'mock_jwt_token_for_hardcode_login',
          sessionData: {
            loginTime: Date.now(),
            ipAddress: '127.0.0.1',
            userAgent: navigator.userAgent,
            sessionId: 'sess_' + Date.now()
          }
        }
        
        console.log('Mock login response data:', mockResponseData)
        loginResponseData = mockResponseData // เก็บข้อมูลไว้ใช้งาน
        
        // สำหรับ hardcode login ไม่ต้องเลือก OM/VC
        console.log('Hardcode login: Skip OM/VC selection, redirect to /cluster')
        storeUserData(mockResponseData.user)
        storeClusterData(mockResponseData.cluster)
        
        // Redirect ไปยังหน้า /cluster
        await router.push('/cluster')
        closeLoginModal()
        isLogging.value = false
        return
      } else {
        // Login ไม่สำเร็จ
        loginErrorMessage.value = 'ชื่อผู้ใช้หรือรหัสผ่านไม่ถูกต้อง (admin/1234)'
        isLogging.value = false
        return
      }
    }

    // เรียกใช้ $DSS plugin สำหรับการ login (site อื่นๆ ที่ไม่ใช่ main)
    const credentials = {
      email: currentLoginForm.value.email,
      password: currentLoginForm.value.password
    }
    
    const response = await $DSS('auth/login', 'POST', credentials)

    if (response && response.success && response.data) {
      // Login สำเร็จ
      console.log('Login successful:', response.data)
      loginResponseData = response.data // เก็บข้อมูลไว้ก่อน

      // ตรวจสอบข้อมูล OM
      if (Array.isArray(loginResponseData.omData) && loginResponseData.omData.length > 0) {
        // มีข้อมูล OM ให้แสดง Modal เพื่อเลือก
        availableOmData.value = loginResponseData.omData
        showSelectionModal.value = true
        // ปิด Login modal แต่ยัง loading อยู่
        isLoginModalOpen.value = false
        // ไม่ต้อง set isLogging = false จนกว่าจะเลือกเสร็จ
      } else {
        // ไม่มีข้อมูล OM ให้เลือก
        console.warn('OM data not found, proceeding without OM/VC selection.')
        storeUserData(loginResponseData.user)
        storeClusterData(loginResponseData.cluster)
        
        // Redirect ไปยังหน้า dashboard
        await router.push('/cluster/user/dashboard')
        closeLoginModal()
        isLogging.value = false
      }
    } else {
      // Login ไม่สำเร็จ
      loginErrorMessage.value = response?.message || 'อีเมลหรือรหัสผ่านไม่ถูกต้อง กรุณาลองใหม่อีกครั้ง'
      console.error('Login failed:', response)
      isLogging.value = false
    }
  } catch (error) {
    // เกิดข้อผิดพลาดในการเชื่อมต่อ API
    loginErrorMessage.value = 'เกิดข้อผิดพลาดในการเชื่อมต่อ กรุณาลองใหม่อีกครั้ง'
    console.error('Login error:', error)
    isLogging.value = false
  }
}

// Function สำหรับเก็บข้อมูล User
const storeUserData = (userData) => {
  if (userData) {
    localStorage.setItem('userData', JSON.stringify(userData))
    localStorage.setItem('loginTime', Date.now().toString())
    
    // เก็บ session data เพิ่มเติมถ้ามี
    if (loginResponseData?.sessionData) {
      localStorage.setItem('sessionData', JSON.stringify(loginResponseData.sessionData))
      console.log('Session data stored:', loginResponseData.sessionData)
    }
    
    // เก็บ token ถ้ามี
    if (loginResponseData?.token) {
      localStorage.setItem('authToken', loginResponseData.token)
      console.log('Auth token stored')
    }
    
    // อัปเดตสถานะการเข้าสู่ระบบ
    currentUser.value = userData
    isLoggedIn.value = true
    // เริ่ม session timer
    startSessionTimer()
    console.log('User data stored:', userData)
  } else {
    console.warn('User data not provided for storage.')
  }
}

// Function สำหรับเก็บข้อมูล Cluster
const storeClusterData = (clusterData) => {
  if (clusterData) {
    localStorage.setItem('clusterData', JSON.stringify(clusterData))
    console.log('Cluster data stored:', clusterData)
  } else {
    console.warn('Cluster data not provided for storage.')
  }
}

// Function ที่จะถูกเรียกเมื่อผู้ใช้ยืนยันการเลือกใน Modal
const handleSelectionConfirmed = async ({ om, vc }) => {
  console.log('Selected OM:', om)
  console.log('Selected VC:', vc)

  try {
    // 1. เก็บข้อมูล User และ Cluster
    storeUserData(loginResponseData?.user)
    storeClusterData(loginResponseData?.cluster)

    // 2. เก็บข้อมูล OM และ VC ที่เลือก
    if (om) {
      localStorage.setItem('activeOmData', JSON.stringify(om))
      activeOmInfo.value = om
      console.log('Active OM data stored:', om)
    }
    if (vc) {
      localStorage.setItem('activeVcData', JSON.stringify(vc))
      activeVcInfo.value = vc
      console.log('Active VC data stored:', vc)
    } else {
      localStorage.removeItem('activeVcData')
      activeVcInfo.value = null
    }

    // 3. เก็บ omData ทั้งหมด
    if (loginResponseData?.omData) {
      localStorage.setItem('omData', JSON.stringify(loginResponseData.omData))
      console.log('Full available OM data stored:', loginResponseData.omData)
    }

    // 4. ซ่อน Modal
    showSelectionModal.value = false
    loginResponseData = null

    // 5. Redirect ไปยังหน้า Dashboard
    await router.push('/cluster/user/dashboard')
  } catch (redirectError) {
    console.error("Redirect error:", redirectError)
    loginErrorMessage.value = "เกิดข้อผิดพลาดในการนำทางไปยังหน้าถัดไป"
  } finally {
    isLogging.value = false
  }
}

// Function ที่จะถูกเรียกเมื่อผู้ใช้ยกเลิก Modal
const handleSelectionCancelled = () => {
  console.log('OM/VC selection cancelled.')
  showSelectionModal.value = false
  isLogging.value = false
  loginResponseData = null
  loginErrorMessage.value = 'การเข้าสู่ระบบถูกยกเลิก'
}

const smoothScrollTo = (target, event) => {
  event?.preventDefault()
  
  // Close mobile menu if open
  isMobileMenuOpen.value = false
  
  // Emit to parent for activeSection update
  emit('smoothScrollTo', target, event)
  
  // Handle smooth scrolling directly here
  if (target === 'home') {
    // Scroll to top for home
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    })
  } else {
    // Find target section and scroll to it
    const element = document.getElementById(target)
    if (element) {
      const headerOffset = 80 // Account for sticky navigation height
      const elementPosition = element.offsetTop
      const offsetPosition = elementPosition - headerOffset

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      })
    }
  }
}



// Handle scroll events for sticky navigation
const handleScroll = () => {
  try {
    const currentScrollY = window.scrollY
    scrollPosition.value = currentScrollY
    isScrolled.value = currentScrollY > 50 // Apply backdrop effects after scrolling
    
    // Force sticky navigation with JavaScript
    const nav = document.querySelector('nav')
    if (nav) {
      const header = document.querySelector('header')
      const headerHeight = header ? header.offsetHeight : 200
      const theme = siteThemes.value
      const baseColor = theme.primary
      
      if (currentScrollY > headerHeight) {
        // Make nav sticky with !important to override CSS
        nav.style.setProperty('position', 'fixed', 'important')
        nav.style.setProperty('top', '0px', 'important')
        nav.style.setProperty('left', '0px', 'important')
        nav.style.setProperty('right', '0px', 'important')
        nav.style.setProperty('width', '100%', 'important')
        nav.style.setProperty('z-index', '1000', 'important')
        nav.style.setProperty('backdrop-filter', 'blur(10px)', 'important')
        nav.style.setProperty('background-color', `rgba(${getColorRGB(baseColor, 600)}, 0.95)`, 'important')
        nav.classList.add('shadow-xl')
      } else {
        // Reset nav to normal position
        nav.style.removeProperty('position')
        nav.style.removeProperty('top')
        nav.style.removeProperty('left')
        nav.style.removeProperty('right')
        nav.style.setProperty('width', '100%', 'important')
        nav.style.removeProperty('backdrop-filter')
        nav.style.setProperty('background-color', `rgb(${getColorRGB(baseColor, 600)})`, 'important')
        nav.classList.remove('shadow-xl')
      }
    }
  } catch (error) {
    console.warn('Error handling scroll event:', error)
  }
}

// ฟังก์ชันตรวจสอบสถานะการเข้าสู่ระบบ
const checkAuthStatus = () => {
  try {
    const userData = localStorage.getItem('userData')
    if (userData) {
      try {
        currentUser.value = JSON.parse(userData)
        isLoggedIn.value = true
        console.log('User already logged in:', currentUser.value)
      } catch (parseError) {
        console.error('Error parsing user data:', parseError)
        localStorage.removeItem('userData')
        return
      }
    }
    
    // โหลดข้อมูล OM/VC ที่เลือกไว้
    try {
      const activeOmData = localStorage.getItem('activeOmData')
      if (activeOmData) {
        activeOmInfo.value = JSON.parse(activeOmData)
      }
    } catch (omError) {
      console.warn('Error parsing OM data:', omError)
      localStorage.removeItem('activeOmData')
    }
    
    try {
      const activeVcData = localStorage.getItem('activeVcData')
      if (activeVcData) {
        activeVcInfo.value = JSON.parse(activeVcData)
      }
    } catch (vcError) {
      console.warn('Error parsing VC data:', vcError)
      localStorage.removeItem('activeVcData')
    }
    
    // โหลดข้อมูล OM ทั้งหมดสำหรับการเปลี่ยน
    try {
      const omData = localStorage.getItem('omData')
      if (omData) {
        availableOmData.value = JSON.parse(omData)
      }
    } catch (omListError) {
      console.warn('Error parsing OM list data:', omListError)
      localStorage.removeItem('omData')
    }
    
    // เริ่มต้น session timer
    if (isLoggedIn.value) {
      startSessionTimer()
    }
  } catch (error) {
    console.error('Error checking auth status:', error)
    // ถ้าข้อมูลใน localStorage เสียหาย ให้ล้างออก
    clearAllAuthData()
  }
}

// ฟังก์ชันล้างข้อมูล auth ทั้งหมด
const clearAllAuthData = () => {
  try {
    localStorage.removeItem('userData')
    localStorage.removeItem('clusterData')
    localStorage.removeItem('activeOmData')
    localStorage.removeItem('activeVcData')
    localStorage.removeItem('omData')
    localStorage.removeItem('sessionData')
    localStorage.removeItem('authToken')
    localStorage.removeItem('loginTime')
  } catch (error) {
    console.warn('Error clearing localStorage:', error)
  }
  
  try {
    isLoggedIn.value = false
    currentUser.value = null
    activeOmInfo.value = null
    activeVcInfo.value = null
    sessionTime.value = ''
  } catch (error) {
    console.warn('Error resetting auth state:', error)
  }
}

// ฟังก์ชันสร้าง initials จากชื่อผู้ใช้
const getUserInitials = (user) => {
  if (user.name) {
    return user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
  } else if (user.email) {
    return user.email.slice(0, 2).toUpperCase()
  }
  return 'U'
}

// ฟังก์ชัน session timer
let sessionInterval = null
const startSessionTimer = () => {
  try {
    // หยุด timer เก่าก่อน (ถ้ามี)
    stopSessionTimer()
    
    // เริ่มจาก login time ถ้ามี หรือเวลาปัจจุบัน
    const loginTime = localStorage.getItem('loginTime') || Date.now().toString()
    if (!localStorage.getItem('loginTime')) {
      localStorage.setItem('loginTime', loginTime)
    }
    
    sessionInterval = setInterval(() => {
      try {
        const elapsed = Date.now() - parseInt(loginTime)
        const minutes = Math.floor(elapsed / 60000)
        const seconds = Math.floor((elapsed % 60000) / 1000)
        sessionTime.value = `${minutes}:${seconds.toString().padStart(2, '0')}`
      } catch (timerError) {
        console.warn('Error updating session timer:', timerError)
        stopSessionTimer()
      }
    }, 1000)
  } catch (error) {
    console.error('Error starting session timer:', error)
  }
}

const stopSessionTimer = () => {
  try {
    if (sessionInterval) {
      clearInterval(sessionInterval)
      sessionInterval = null
    }
  } catch (error) {
    console.warn('Error stopping session timer:', error)
  }
}

// ฟังก์ชันเปิด Modal เลือก OM/VC ใหม่
const openOmVcSelection = () => {
  if (availableOmData.value.length > 0) {
    showSelectionModal.value = true
  }
}

// ฟังก์ชัน Logout
const handleLogout = async () => {
  try {
    // หยุด session timer
    stopSessionTimer()
    
    // ล้างข้อมูลทั้งหมด
    clearAllAuthData()
    localStorage.removeItem('loginTime')
    
    console.log('User logged out successfully')
    
    // Redirect ไปหน้าหลัก
    await router.push('/')
  } catch (error) {
    console.error('Error during logout:', error)
    // ถึงแม้จะ error ก็ยังให้ล้างข้อมูลและ redirect
    try {
      window.location.href = '/'
    } catch (redirectError) {
      console.error('Error redirecting after logout:', redirectError)
    }
  }
}

// Setup sticky navigation on mount
onMounted(() => {
  console.log('SiteHeader mounted, setting up sticky navigation...')
  
  // ตรวจสอบสถานะการเข้าสู่ระบบเมื่อ component mount
  checkAuthStatus()
  
  // Add scroll listener for sticky behavior
  window.addEventListener('scroll', handleScroll, { passive: true })
  
  // Initial scroll check
  handleScroll()
  
  // Log initial state
  console.log('Initial scroll position:', window.scrollY)
  
  // Test if navigation element exists
  const nav = document.querySelector('nav')
  if (nav) {
    // Apply initial sticky styling
    nav.style.position = 'sticky'
    nav.style.top = '0'
    nav.style.zIndex = '1000'
    
    // Check computed style after a delay
    setTimeout(() => {
      const computedPosition = window.getComputedStyle(nav).position
      console.log('Navigation position:', {
        set: nav.style.position,
        computed: computedPosition,
        stickySupported: computedPosition === 'sticky'
      })
      
      if (computedPosition !== 'sticky') {
        console.log('CSS sticky not supported, using JS fallback')
      }
    }, 100)
  }
  
  console.log('Sticky navigation initialized')
})

// Cleanup on unmount
onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll)
  
  // Stop session timer
  stopSessionTimer()
  
  // Clean up sticky styles - ใช้ querySelector แทน navElement ref
  try {
    const nav = document.querySelector('nav')
    if (nav) {
      nav.style.position = ''
      nav.style.top = ''
      nav.style.left = ''
      nav.style.right = ''
      nav.style.width = ''
      nav.style.removeProperty('backdrop-filter')
      nav.style.removeProperty('background-color')
    }
  } catch (error) {
    console.warn('Error cleaning up navigation styles:', error)
  }
  
  // Remove body padding
  try {
    document.body.style.paddingTop = '0'
  } catch (error) {
    console.warn('Error removing body padding:', error)
  }
})

// Computed properties for theming based on currentSite
const siteThemes = computed(() => {
  const themes = {
    'main': {
      primary: 'green',
      primaryClass: 'green-600',
      primaryDarkClass: 'green-700',
      primaryLightClass: 'green-100',
      borderClass: 'green-500',
      hoverBgClass: 'hover:bg-green-100',
      activeBorderClass: 'border-yellow-300'
    },
    'halal': {
      primary: 'emerald',
      primaryClass: 'emerald-600',
      primaryDarkClass: 'emerald-700',
      primaryLightClass: 'emerald-100',
      borderClass: 'emerald-500',
      hoverBgClass: 'hover:bg-emerald-100',
      activeBorderClass: 'border-yellow-300'
    },
    'fruit': {
      primary: 'orange',
      primaryClass: 'orange-600',
      primaryDarkClass: 'orange-700',
      primaryLightClass: 'orange-100',
      borderClass: 'orange-500',
      hoverBgClass: 'hover:bg-orange-100',
      activeBorderClass: 'border-yellow-300'
    },
    'coffee': {
      primary: 'amber',
      primaryClass: 'amber-600',
      primaryDarkClass: 'amber-700',
      primaryLightClass: 'amber-100',
      borderClass: 'amber-500',
      hoverBgClass: 'hover:bg-amber-100',
      activeBorderClass: 'border-yellow-300'
    },
    'fisheries': {
      primary: 'blue',
      primaryClass: 'blue-600',
      primaryDarkClass: 'blue-700',
      primaryLightClass: 'blue-100',
      borderClass: 'blue-500',
      hoverBgClass: 'hover:bg-blue-100',
      activeBorderClass: 'border-yellow-300'
    },
    'bittermelon': {
      primary: 'green',
      primaryClass: 'green-600',
      primaryDarkClass: 'green-700',
      primaryLightClass: 'green-100',
      borderClass: 'green-500',
      hoverBgClass: 'hover:bg-green-100',
      activeBorderClass: 'border-yellow-300'
    },
    'mulberry': {
      primary: 'purple',
      primaryClass: 'purple-600',
      primaryDarkClass: 'purple-700',
      primaryLightClass: 'purple-100',
      borderClass: 'purple-500',
      hoverBgClass: 'hover:bg-purple-100',
      activeBorderClass: 'border-yellow-300'
    }
  }
  return themes[props.currentSite] || themes['main']
})

// Navigation styling computed properties
const navClasses = computed(() => {
  return `bg-${siteThemes.value.primary}-600`
})

const navStyles = computed(() => {
  const theme = siteThemes.value
  const baseColor = theme.primary
  
  return {
    backdropFilter: isScrolled.value ? 'blur(12px)' : 'none',
    backgroundColor: isScrolled.value 
      ? `rgba(${getColorRGB(baseColor, 600)}, 0.95)`
      : `rgb(${getColorRGB(baseColor, 600)})`,
    boxShadow: isScrolled.value 
      ? '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)' 
      : '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)'
  }
})

const getColorRGB = (color, shade) => {
  const colorMap = {
    'green': {
      600: '22, 163, 74',
      700: '21, 128, 61'
    },
    'emerald': {
      600: '5, 150, 105',
      700: '4, 120, 87'
    },
    'orange': {
      600: '234, 88, 12',
      700: '194, 65, 12'
    },
    'amber': {
      600: '217, 119, 6',
      700: '180, 83, 9'
    },
    'blue': {
      600: '37, 99, 235',
      700: '29, 78, 216'
    },
    'purple': {
      600: '147, 51, 234',
      700: '126, 34, 206'
    }
  }
  return colorMap[color]?.[shade] || colorMap['green'][shade]
}

const navHoverClasses = computed(() => {
  return `hover:text-${siteThemes.value.primary}-100`
})

const loginButtonClasses = computed(() => {
  return `hover:text-${siteThemes.value.primary}-100 hover:bg-${siteThemes.value.primary}-700`
})

const mobileMenuButtonClasses = computed(() => {
  return `hover:text-${siteThemes.value.primary}-100 focus:text-${siteThemes.value.primary}-100`
})

const mobileMenuBorderClasses = computed(() => {
  return `border-${siteThemes.value.primary}-500`
})

const mobileMenuBgClasses = computed(() => {
  return `bg-${siteThemes.value.primary}-600`
})

const mobileNavItemHoverClasses = computed(() => {
  return `hover:bg-${siteThemes.value.primary}-700 hover:text-${siteThemes.value.primary}-100`
})

// Methods for navigation item classes
const getNavItemClasses = (section) => {
  const baseClasses = navHoverClasses.value
  const activeClasses = props.activeSection === section 
    ? 'text-yellow-300 border-b-2 border-yellow-300' 
    : ''
  return `${baseClasses} ${activeClasses}`
}

const getMobileNavItemClasses = (section) => {
  const theme = siteThemes.value
  const baseClasses = `hover:bg-${theme.primary}-700 hover:text-${theme.primary}-100`
  const activeClasses = props.activeSection === section 
    ? `bg-${theme.primary}-700 text-yellow-300` 
    : ''
  return `${baseClasses} ${activeClasses}`
}

// Modal styling computed properties
const formInputClasses = computed(() => {
  return `w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-${siteThemes.value.primary}-500 focus:border-${siteThemes.value.primary}-500`
})

const loginSubmitButtonClasses = computed(() => {
  return `w-full bg-${siteThemes.value.primary}-600 text-white py-2 px-4 rounded-md hover:bg-${siteThemes.value.primary}-700 transition-colors disabled:opacity-50`
})

const contactModalButtonClasses = computed(() => {
  return `w-full bg-${siteThemes.value.primary}-600 text-white py-2 px-4 rounded hover:bg-${siteThemes.value.primary}-700 transition-colors`
})

const linkClasses = computed(() => {
  return `text-${siteThemes.value.primary}-600 hover:text-${siteThemes.value.primary}-700`
})

// Computed properties for cluster names based on currentSite
const clusterTitle = computed(() => {
  const clusterNames = {
    'main': 'Cluster SIE',
    'coffee': 'คลัสเตอร์กาแฟ',
    'fisheries': 'คลัสเตอร์ประมง',
    'fruit': 'คลัสเตอร์ผักและผลไม้',
    'bittermelon': 'คลัสเตอร์มะระ',
    'halal': 'คลัสเตอร์ฮาลาล',
    'mulberry': 'คลัสเตอร์หม่อนไหม'
  }
  return clusterNames[props.currentSite] || 'Cluster SIE'
})

const clusterSubtitle = computed(() => {
  const subtitles = {
    'main': 'โครงการพัฒนาเครือข่ายธุรกิจชุมชน (Cluster) สู่เศรษฐกิจหมุนเวียน',
    'coffee': 'โครงการพัฒนาเครือข่ายธุรกิจชุมชน (Cluster) สู่เศรษฐกิจหมุนเวียน',
    'fisheries': 'โครงการพัฒนาเครือข่ายธุรกิจชุมชน (Cluster) สู่เศรษฐกิจหมุนเวียน',
    'fruit': 'โครงการพัฒนาเครือข่ายธุรกิจชุมชน (Cluster) สู่เศรษฐกิจหมุนเวียน',
    'bittermelon': 'โครงการพัฒนาเครือข่ายธุรกิจชุมชน (Cluster) สู่เศรษฐกิจหมุนเวียน',
    'halal':'โครงการพัฒนาเครือข่ายธุรกิจชุมชน (Cluster) สู่เศรษฐกิจหมุนเวียน',
    'mulberry': 'โครงการพัฒนาเครือข่ายธุรกิจชุมชน (Cluster) สู่เศรษฐกิจหมุนเวียน',
  }
  return subtitles[props.currentSite] || 'โครงการพัฒนาเครือข่ายธุรกิจชุมชน (Cluster) สู่เศรษฐกิจหมุนเวียน'
})

const clusterLogo = computed(() => {
  const logos = {
    'main': 'https://vue-project.sgp1.digitaloceanspaces.com/2025/10/1761076391316.png',
    'coffee': 'https://vue-project.sgp1.digitaloceanspaces.com/2025/10/1761076391316.png', // แทนที่ด้วย logo กาแฟ
    'fisheries': 'https://vue-project.sgp1.digitaloceanspaces.com/2025/10/1761076391316.png', // แทนที่ด้วย logo ประมง
    'fruit': 'https://vue-project.sgp1.digitaloceanspaces.com/2025/10/1761076391316.png', // แทนที่ด้วย logo ผัก-ผลไม้
    'bittermelon': 'https://vue-project.sgp1.digitaloceanspaces.com/2025/10/1761076391316.png', // แทนที่ด้วย logo มะระ
    'halal': 'https://vue-project.sgp1.digitaloceanspaces.com/2025/10/1761076391316.png', // แทนที่ด้วย logo ฮาลาล
    'mulberry': 'https://vue-project.sgp1.digitaloceanspaces.com/2025/10/1761076391316.png' // แทนที่ด้วย logo หม่อนไหม
  }
  return logos[props.currentSite] || 'https://vue-project.sgp1.digitaloceanspaces.com/2025/10/1761076391316.png'
})

const clusterAltText = computed(() => {
  return `${clusterTitle.value} Logo`
})

// Computed property for header container classes
const headerContainerClasses = computed(() => {
  if (props.mode === 'pages') {
    // Full width for pages mode
    return 'relative w-full px-4 sm:px-6 lg:px-8'
  } else {
    // Constrained width for normal mode
    return 'relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'
  }
})

// Computed property for user bar container classes
const userBarContainerClasses = computed(() => {
  if (props.mode === 'pages') {
    // Full width for pages mode
    return 'w-full px-4 sm:px-6 lg:px-8'
  } else {
    // Constrained width for normal mode
    return 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'
  }
})

// Computed property for header opacity
const headerOpacity = computed(() => {
  if (!isScrolled.value) return 1
  // Calculate opacity based on scroll position (fade effect)
  const maxScroll = 200
  const opacity = Math.max(0.95, 1 - (scrollPosition.value / maxScroll) * 0.05)
  return opacity
})


</script>

<style scoped>
/* Solar System specific styles */
.solar-sun {
  filter: drop-shadow(0 0 6px rgba(255, 213, 79, 0.4));
}

.central-connection {
  animation: centralPulse 3s ease-in-out infinite;
  filter: drop-shadow(0 0 3px rgba(129, 199, 132, 0.4));
}

@keyframes centralPulse {
  0%, 100% {
    opacity: 0.5;
  }
  50% {
    opacity: 0.8;
  }
}

/* Mobile Menu Animations */
.mobile-menu-enter-active,
.mobile-menu-leave-active {
  transition: all 0.3s ease-in-out;
}

.mobile-menu-enter-from {
  opacity: 0;
  transform: translateY(-10px);
}

.mobile-menu-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* Mobile menu items */
.mobile-menu-item {
  transition: all 0.2s ease-in-out;
}

.mobile-menu-item:hover {
  transform: translateX(4px);
}

/* Sticky Navigation */
.nav-sticky {
  position: -webkit-sticky !important;
  position: sticky !important;
  top: 0 !important;
  z-index: 1000 !important;
  width: 100% !important;
  will-change: transform;
}



/* Sticky state for JS fallback */
nav.js-sticky {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  z-index: 1000 !important;
  width: 100% !important;
}

/* Performance optimizations */
* {
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

/* Navigation animations */
.nav-item {
  transition: all 0.2s ease-in-out;
}

.nav-item:hover {
  transform: translateY(-1px);
}

/* Responsive adjustments */
@media (max-width: 768px) {
  /* Hide desktop menu items on mobile */
  .desktop-menu {
    display: none;
  }
}

@media (max-width: 640px) {
  /* Adjust mobile menu for smaller screens */
  .mobile-menu-panel {
    margin-left: 0.5rem;
    margin-right: 0.5rem;
  }
}
</style>